'use client';

import { useId } from 'react';

import { cn } from '@/lib/utils';

import { WithRef } from '@/cms/types/types';

import InputTooltip from '../Input/InputTooltip';
import { TooltipProps } from '../Tooltip/Tooltip';

import iconCheckboxSrc from '~/svg/IconCheckboxDecodo.svg';

import { SvgIcon } from '@/components/SvgIcon';
export const sizes = ['sm', 'md'] as const;
export type Size = (typeof sizes)[number];

export type CheckboxProps = {
  id?: string;
  classNames?: {
    root?: string;
    label?: string;
    checkboxIcon?: string;
    tooltip?: TooltipProps['classNames'];
  };
  size?: Size;
  label?: React.ReactNode;
  tooltip?: TooltipProps['text'];
  isDisabled?: boolean;
  value: string;
  checked?: boolean;
  error?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const getSizeBasedStyle = (size: Size) => {
  return {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
  }[size];
};

const getCheckboxInputColor = (
  hasError: boolean,
  isDisabled: boolean,
  checked: boolean
) => {
  // error should take precedence, do not rearrange
  if (hasError) {
    return 'bg-base-white checked:bg-transparent border-error-500 checked:border-error-500';
  }
  if (isDisabled) {
    return cn(
      'bg-gray-50 checked:bg-transparent  border-gray-300 dark:border-gray-200',
      checked && 'border-gray-500 dark:border-gray-400'
    );
  }

  return cn(
    'bg-base-white checked:bg-transparent checked:border-brand-500 border-gray-400',
    !checked && 'hover:border-gray-500'
  );
};

const getCheckIconColor = (
  hasError: boolean,
  isDisabled: boolean,
  checked: boolean
) => {
  // error should take precedence, do not rearrange
  if (hasError) {
    return 'bg-base-white rounded-sm text-error-500 fill-transparent';
  }
  if (isDisabled) {
    return cn(
      'bg-gray-50 rounded-sm text-gray-500 dark:text-gray-400 fill-transparent',
      checked && 'border-gray-500 dark:border-gray-400'
    );
  }

  return 'dark:bg-base-dark-white bg-base-white rounded text-brand-500 fill-transparent';
};

const getLabelColor = (isDisabled: boolean) => {
  if (isDisabled) {
    return 'text-gray-500 dark:text-gray-400';
  }

  return 'text-gray-950 ';
};

export const Checkbox = ({
  classNames,
  label,
  isDisabled,
  value,
  onChange,
  checked,
  ref,
  size = 'md',
  error,
  ...props
}: WithRef<HTMLInputElement, CheckboxProps>) => {
  const id = useId();

  return (
    <div
      className='relative flex items-center gap-x-2'
      data-tracker-component='checkbox'
    >
      <div>
        <input
          id={id}
          disabled={isDisabled}
          type='checkbox'
          className={cn(
            'absolute flex appearance-none items-center rounded-sm border',
            getSizeBasedStyle(size),
            getCheckboxInputColor(!!error, !!isDisabled, !!checked),
            classNames?.root
          )}
          value={value}
          checked={checked}
          onChange={onChange}
          ref={ref}
          {...props}
        />
        <div className='flex items-center justify-center'>
          <SvgIcon
            src={iconCheckboxSrc}
            className={cn(
              getSizeBasedStyle(size),
              getCheckIconColor(!!error, !!isDisabled, !!checked),
              classNames?.checkboxIcon
            )}
          />
        </div>
      </div>
      <label
        htmlFor={id}
        className={cn(
          getLabelColor(!!isDisabled),
          size === 'sm' ? 'text-body-s' : 'text-body-m',
          classNames?.label
        )}
      >
        {label}
      </label>
      {props.tooltip ? (
        <InputTooltip
          tooltip={props.tooltip}
          classNames={classNames?.tooltip}
          flowDirection='bottom-center'
        />
      ) : null}
    </div>
  );
};

export default Checkbox;
